import { Fragment, lazy, Suspense, useMemo, useState, type ReactNode } from 'react';
import { useCustomEmoji } from '@/hooks/queries';
import { copyText } from '@/lib/clipboard';
import { toast } from '@/store/ui';
import { cn } from '@/lib/utils';
import { parseInline, parseMarkdown, type Block, type Inline } from '@itsappy/shared';
import { highlight } from '@/lib/highlight';
import { ArrowForwardIcon } from '@/components/ui/icons';

/** Mermaid is ~2MB; it loads only when a message actually contains one. */
const MermaidBlock = lazy(() =>
  import('./MermaidBlock').then((module) => ({ default: module.MermaidBlock })),
);

/** Longer than this and a code block collapses until asked to open. */
const CODE_COLLAPSE_LINES = 14;

const sharedLocationPattern =
  /^My location:\s+(https:\/\/www\.google\.com\/maps\?q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?))\s*$/i;

export interface MarkdownProps {
  body: string;
  /** Resolves `<@id>` mentions to names. */
  names: Map<string, string>;
  /** Highlights mentions of the reader. */
  viewerId?: string;
  className?: string;
}

/**
 * Renders a message body.
 *
 * The parser produces a typed tree and this walks it into React elements, so
 * nothing a person types can become markup — there is no `dangerouslySetInnerHTML`
 * anywhere in this path, and therefore no sanitiser to get wrong.
 */
export function Markdown({ body, names, viewerId, className }: MarkdownProps) {
  const location = useMemo(() => sharedLocationPattern.exec(body.trim()), [body]);
  const blocks = useMemo(() => parseMarkdown(body), [body]);
  if (location) {
    return (
      <LocationCard
        href={location[1]}
        latitude={location[2]}
        longitude={location[3]}
        className={className}
      />
    );
  }
  return (
    <div className={cn('space-y-1.5 break-words', className)}>
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} names={names} viewerId={viewerId} />
      ))}
    </div>
  );
}

function LocationCard({
  href,
  latitude,
  longitude,
  className,
}: {
  href: string;
  latitude: string;
  longitude: string;
  className?: string;
}) {
  const coordinateLabel = `${Number(latitude).toFixed(4)}, ${Number(longitude).toFixed(4)}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={`Open shared location at ${coordinateLabel} in Maps`}
      className={cn(
        'group block w-full min-w-56 max-w-72 overflow-hidden rounded-xl bg-surface-2 shadow-md shadow-black/15 transition-[background-color,transform] duration-150 hover:-translate-y-0.5 hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
        className,
      )}
    >
      <span className="grid min-h-24 place-items-center bg-brand-500/10">
        <span className="ink-link grid h-12 w-12 place-items-center rounded-full bg-surface-1 shadow-md shadow-black/15">
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
            <path
              d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </span>
      </span>
      <span className="block p-3">
        <span className="block text-sm font-semibold text-slate-100">Shared location</span>
        <span className="mt-0.5 block text-xs tabular-nums text-slate-400">
          {coordinateLabel}
        </span>
        <span className="ink-link mt-3 flex items-center justify-between border-t border-surface-4 pt-2 text-xs font-semibold">
          Open in Maps
          <ArrowForwardIcon size="sm" />
        </span>
      </span>
    </a>
  );
}

/** Inline-only rendering, for places with no room for block layout. */
export function MarkdownInline({ body, names, viewerId }: Omit<MarkdownProps, 'className'>) {
  const nodes = useMemo(() => parseInline(body), [body]);
  return <InlineList nodes={nodes} names={names} viewerId={viewerId} />;
}

function BlockView({
  block,
  names,
  viewerId,
}: {
  block: Block;
  names: Map<string, string>;
  viewerId?: string;
}) {
  switch (block.type) {
    case 'paragraph':
      return (
        <p className="whitespace-pre-wrap text-[0.9375rem] leading-[1.55] text-slate-100">
          <InlineList nodes={block.children} names={names} viewerId={viewerId} />
        </p>
      );
    case 'heading': {
      const size =
        block.level === 1 ? 'text-base' : 'text-[0.9375rem]';
      return (
        <p className={cn('font-semibold text-slate-100', size)}>
          <InlineList nodes={block.children} names={names} viewerId={viewerId} />
        </p>
      );
    }
    case 'quote':
      return (
        <blockquote className="border-l-2 border-surface-4 pl-2.5 text-slate-300">
          {block.children.map((child, index) => (
            <BlockView key={index} block={child} names={names} viewerId={viewerId} />
          ))}
        </blockquote>
      );
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag
          className={cn(
            'ml-4 space-y-0.5 text-[0.9375rem] text-slate-100',
            block.ordered ? 'list-decimal' : 'list-disc',
          )}
        >
          {block.items.map((item, index) => (
            <li key={index} className="leading-[1.55]">
              <InlineList nodes={item} names={names} viewerId={viewerId} />
            </li>
          ))}
        </Tag>
      );
    }
    case 'code':
      return block.language === 'mermaid' ? (
        <Suspense
          fallback={
            <pre className="rounded-lg bg-surface-2 p-2 text-xs text-slate-500">
              Rendering diagram…
            </pre>
          }
        >
          <MermaidBlock source={block.value} />
        </Suspense>
      ) : (
        <CodeBlock language={block.language} value={block.value} />
      );
    case 'rule':
      return <hr className="border-surface-3" />;
    default:
      return null;
  }
}

function InlineList({
  nodes,
  names,
  viewerId,
}: {
  nodes: Inline[];
  names: Map<string, string>;
  viewerId?: string;
}) {
  return (
    <>
      {nodes.map((node, index) => (
        <InlineView key={index} node={node} names={names} viewerId={viewerId} />
      ))}
    </>
  );
}

function InlineView({
  node,
  names,
  viewerId,
}: {
  node: Inline;
  names: Map<string, string>;
  viewerId?: string;
}): ReactNode {
  switch (node.type) {
    case 'text':
      return <>{node.value}</>;
    case 'strong':
      return (
        <strong className="font-semibold text-slate-100">
          <InlineList nodes={node.children} names={names} viewerId={viewerId} />
        </strong>
      );
    case 'em':
      return (
        <em className="italic">
          <InlineList nodes={node.children} names={names} viewerId={viewerId} />
        </em>
      );
    case 'strike':
      return (
        <s className="text-slate-500">
          <InlineList nodes={node.children} names={names} viewerId={viewerId} />
        </s>
      );
    case 'code':
      return (
        <code className="rounded bg-surface-3 px-1 py-0.5 font-mono text-[0.85em] ink-brand">
          {node.value}
        </code>
      );
    case 'spoiler':
      return (
        <Spoiler>
          <InlineList nodes={node.children} names={names} viewerId={viewerId} />
        </Spoiler>
      );
    case 'link':
      return (
        <a
          href={node.href}
          target="_blank"
          rel="noreferrer noopener"
          className="link-inline"
        >
          {node.label}
        </a>
      );
    case 'mention': {
      const isMe = viewerId === node.userId;
      return (
        <span
          className={cn(
            'rounded px-1 py-px text-[0.95em] font-medium',
            isMe ? 'tone-amber' : 'tone-brand',
          )}
        >
          @{names.get(node.userId) ?? 'someone'}
        </span>
      );
    }
    /* Always highlighted as addressed to the reader: `@here` and `@channel`
       are aimed at everyone who can see them, so there is no "someone else's
       mention" version of this to grey out. */
    case 'broadcast':
      return (
        <span
          className="tone-amber rounded px-1 py-px text-[0.95em] font-medium"
          title={
            node.keyword === 'here'
              ? 'Everyone online in this conversation'
              : 'Everyone in this conversation'
          }
        >
          @{node.keyword}
        </span>
      );
    case 'emoji':
      return <CustomEmojiGlyph name={node.name} />;
    default:
      return null;
  }
}

/** `:shipit:` renders as the org's image, or stays literal if there isn't one. */
function CustomEmojiGlyph({ name }: { name: string }) {
  const { data } = useCustomEmoji();
  const emoji = data?.items.find((item) => item.name === name.toLowerCase());
  if (!emoji) return <>:{name}:</>;
  return (
    <img
      src={emoji.url}
      alt={`:${name}:`}
      title={`:${name}:`}
      className="inline-block h-[1.25em] w-[1.25em] object-contain align-text-bottom"
    />
  );
}

function Spoiler({ children }: { children: ReactNode }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setRevealed(true)}
      aria-label={revealed ? undefined : 'Reveal hidden text'}
      className={cn(
        'rounded px-1 transition-colors',
        revealed
          ? 'cursor-text bg-surface-3/60'
          : 'cursor-pointer select-none bg-surface-4 text-transparent hover:bg-surface-4/80',
      )}
    >
      {children}
    </button>
  );
}

/**
 * A fenced code block: highlighted, copyable, and collapsed when long — a
 * pasted stack trace should not push the rest of the conversation off screen.
 */
function CodeBlock({ language, value }: { language?: string; value: string }) {
  const lines = value.split('\n');
  const long = lines.length > CODE_COLLAPSE_LINES;
  const [expanded, setExpanded] = useState(!long);
  const shown = expanded ? value : lines.slice(0, CODE_COLLAPSE_LINES).join('\n');
  const tokens = useMemo(() => highlight(shown, language), [shown, language]);

  return (
    <div className="overflow-hidden rounded-lg ring-1 ring-surface-3">
      <div className="flex items-center gap-2 bg-surface-2 px-2 py-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">
          {language || 'code'}
        </span>
        <span className="ml-auto flex items-center gap-1">
          {long && (
            <button
              type="button"
              onClick={() => setExpanded((open) => !open)}
              className="rounded px-1.5 py-0.5 text-[11px] font-medium text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
            >
              {expanded ? 'Collapse' : `Show all ${lines.length} lines`}
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              try {
                await copyText(value);
                toast.success('Code copied');
              } catch {
                toast.error('Could not copy');
              }
            }}
            className="rounded px-1.5 py-0.5 text-[11px] font-medium text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
          >
            Copy
          </button>
        </span>
      </div>
      <pre className="overflow-x-auto bg-surface-1 p-2.5 text-xs leading-relaxed">
        <code className="font-mono">
          {tokens.map((token, index) => (
            <Fragment key={index}>
              {token.className ? (
                <span className={token.className}>{token.value}</span>
              ) : (
                token.value
              )}
            </Fragment>
          ))}
        </code>
      </pre>
      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full bg-surface-2 py-1 text-[11px] text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          {lines.length - CODE_COLLAPSE_LINES} more lines
        </button>
      )}
    </div>
  );
}
